import React from 'react';
import { planName, planPrice, FEATURE_MIN_PLAN } from '../utils/plans';
import { useNavigate } from 'react-router-dom';

const FEATURE_TEASERS = {
  holdings_full: {
    icon: '💰',
    title: 'Holdings Insight',
    teaser: 'See fair value estimates, analyst price targets, consensus ratings, and insider buy/sell activity for every position in your portfolio.',
    tier: 'Pro',
  },
  dividend_tracker: {
    icon: '💵',
    title: 'Dividend Tracker',
    teaser: 'Track dividend income across your portfolio — per-share amounts, annual yield, upcoming ex-dates, and projected income based on your actual share count.',
    tier: 'Pro',
  },
  etf_overlap: {
    icon: '🔄',
    title: 'ETF Overlap Analysis',
    teaser: 'Discover hidden concentration risk — see which of your individual stocks also appear in major ETFs like SPY, QQQ, and SMH.',
    tier: 'Pro',
  },
  ai_chat: {
    icon: '🤖',
    title: 'AI Portfolio Chat',
    teaser: 'Ask questions about your portfolio scores, get position-specific advice, and explore trade ideas with an AI that understands your holdings.',
    tier: 'Pro',
  },
  trade_simulator: {
    icon: '⚖️',
    title: 'Trade Simulator',
    teaser: 'Test "what if" scenarios — simulate selling one position and buying another to see how it changes your portfolio\'s conviction scores and allocation.',
    tier: 'Pro',
  },
  benchmark: {
    icon: '📊',
    title: 'Benchmark Comparison',
    teaser: 'Compare your portfolio\'s total return against the S&P 500 across multiple time periods. Are you beating the market?',
    tier: 'Pro',
  },
  portfolio_builder: {
    icon: '🔨',
    title: 'Portfolio Builder',
    teaser: 'Build a hypothetical portfolio from scratch — enter tickers and shares, and run the full analysis before committing any capital.',
    tier: 'Starter',
  },
  score_history: {
    icon: '📈',
    title: 'Score History',
    teaser: 'Track how your portfolio\'s conviction scores change over time. Spot trends and see if your positions are improving or deteriorating.',
    tier: 'Premium',
  },
};

export default function UpgradePrompt({ feature, message }) {
  // The real lowest plan with this feature (utils/plans.js). The teasers'
  // own `tier` fields were the retired Starter/Pro names and some were wrong
  // (Score History said Premium; Analyst has it).
  const minPlan = FEATURE_MIN_PLAN[feature] || 'premium';
  const navigate = useNavigate();
  const teaser = FEATURE_TEASERS[feature] || {
    icon: '🔒',
    title: 'Premium Feature',
    teaser: message || 'This feature requires a plan upgrade.',
    tier: 'Pro',
  };

  return (
    <div
      onClick={() => navigate('/pricing')}
      style={{
        background: 'linear-gradient(135deg, rgba(100,149,237,0.06), rgba(0,212,170,0.04))',
        border: '1px solid rgba(100,149,237,0.25)',
        borderRadius: 14,
        padding: '2rem',
        marginBottom: '1.5rem',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = '#6495ED';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 8px 25px rgba(100, 149, 237, 0.15)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'rgba(100,149,237,0.25)';
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
        <span style={{ fontSize: '2rem', lineHeight: 1 }}>{teaser.icon}</span>
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 0.4rem', fontSize: '1.05rem', fontWeight: 700, color: '#F1F5F9' }}>
            {teaser.title}
          </h4>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#94A3B8', lineHeight: 1.6 }}>
            {teaser.teaser}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{
              padding: '0.4rem 1rem',
              background: '#6495ED',
              color: 'white',
              borderRadius: 6,
              fontWeight: 700,
              fontSize: '0.82rem',
              transition: 'all 0.15s ease',
            }}>
              {minPlan === 'free' ? 'Unlock with a free account' : `Unlock with ${planName(minPlan)}`}
            </span>
            {minPlan !== 'free' && (
              <span style={{ fontSize: '0.75rem', color: '#64748B' }}>
                Starting at {planPrice(minPlan)}/month
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
